import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useUIStore } from '../../lib/stores/ui-store';
import { useAuthStore } from '../../lib/stores/auth-store';
import { useRBAC, useLoadPermissions } from '../../hooks/use-rbac';
import { type PermissionCategory } from '../../lib/config/permissions';
import { hasPermission, type UserRole, type NavCategory } from '../../lib/config/rbac-permissions';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  VisuallyHidden,
} from '../ui/sheet';
import {
  LayoutDashboard,
  Truck,
  Users,
  Building2,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Map,
  DollarSign,
  BarChart3,
  Shield,
  Handshake,
  FolderOpen,
  ClipboardCheck,
  Search,
  MapPin,
  HelpCircle,
  Bookmark,
  Plus,
  Store,
  Cog,
  Settings,
  Receipt,
  Calculator,
  BookOpen,
  Plug,
  Wrench,
  Package,
  AlertTriangle,
  ExternalLink,
  Fuel,
  Warehouse,
  Container,
} from 'lucide-react';
import { useUserStore } from '../../lib/stores/user-store';
import { useOrganizationStore } from '../../lib/stores/organization-store';
import { useState } from 'react';
import { useQuickbooksStatus } from '../../lib/api/quickbooks';
import { useFleetrockStatus } from '../../lib/api/fleetrock';
import { useRelayStatus } from '../../lib/api/relay';
import { GlobalSearch } from '../common/global-search';
import { APP_NAME } from '../../lib/helpers';
import logo from '../../assets/logo1.png';
import { getShortcutLabel, MOD_SYMBOL } from '../../lib/config/keyboard-shortcuts';
interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number; // Support text badges like "Try Demo"
  badgeVariant?: 'default' | 'warning'; // warning = red triangle + number
  shortcut?: string; // Keyboard shortcut like "⌘E" or "⌘D"
  carrierOnly?: boolean;
  brokerOnly?: boolean;
  permission?: PermissionCategory; // Permission required to see this item
  isCreateAction?: boolean; // If true, check canCreate instead of canView
  children?: NavItem[]; // Nested sub-items
  openInNewWindow?: boolean; // Show external link icon, opens in new window
}

interface NavGroup {
  title: string;
  items: NavItem[];
  collapsible?: boolean; // Whether this section can be collapsed
  defaultCollapsed?: boolean; // Default collapsed state
}

// Top quick actions (above divider)
const getTopActions = (): NavItem[] => [
  { title: 'Home', href: '/', icon: LayoutDashboard, permission: 'dashboard' },
  { title: 'Search', href: '/search', icon: Search, shortcut: `${MOD_SYMBOL}K`, permission: 'dashboard' },
  { title: 'New Load', href: '/loads/new', icon: Plus, shortcut: getShortcutLabel('/loads/new'), permission: 'loads', isCreateAction: true },
];

// Main navigation (below divider)
const getNavigation = (businessMode: 'carrier' | 'broker' | 'customer_portal', integrations: { qbConnected: boolean; fleetrockConnected: boolean; relayConnected: boolean }): NavItem[] => [
  {
    title: 'Loads & Trips',
    href: '/loads',
    icon: MapPin,
    permission: 'loads',
    children: [
      { title: 'Dispatch Load & Trips', href: '/loads', icon: Package, shortcut: getShortcutLabel('/loads'), permission: 'loads' },
      // { title: 'Tenders', href: '/coming-soon?feature=Tenders', icon: FileText, permission: 'loads', badge: 'Coming Soon' },
      // { title: 'Rate Analytics', href: '/coming-soon?feature=Rate%20Analytics', icon: BarChart3, permission: 'analytics', badge: 'Coming Soon' },
      // { title: 'Marketplace', href: '/coming-soon?feature=Marketplace', icon: Store, permission: 'marketplace', badge: 'Coming Soon' },
      { title: 'Load Board', href: '/dispatch', icon: Store, permission: 'dispatch' },
      { title: 'Load Calculator', href: '/loads/calculator', icon: Calculator, permission: 'loads' },
    ]
  },
  {
    title: 'Accounting',
    href: '/invoices',
    icon: FileText,
    children: [
      { title: 'Invoices', href: '/invoices', icon: FileText, shortcut: getShortcutLabel('/invoices'), permission: 'invoices' },
      { title: 'Aging report', href: '/reports/aging', icon: BarChart3, permission: 'reports' },
      { title: 'Factoring Batches', href: '/factoring', icon: FileText, permission: 'invoices' },
      {
        title: 'Driver Settlements', href: '/settlements', icon: DollarSign, permission: 'settlements'
      },
      // { title: 'Error Transactions', href: '/coming-soon?feature=Error%20Transactions', icon: FileText, permission: 'invoices' },
      // { title: 'Echecks', href: '/coming-soon?feature=Echecks', icon: FileText, permission: 'invoices' },
      { title: 'Expenses', href: '/expenses', icon: Receipt, permission: 'expenses' },
      { title: 'Fuel & Charges', href: '/fuel', icon: Receipt, permission: 'expenses' },
    ]
  },
  {
    title: 'Reports',
    href: '/reports',
    icon: BarChart3,
    permission: 'reports',
    children: [
      { title: 'All Reports', href: '/reports', icon: BarChart3, permission: 'reports' },
      // { title: 'Analytics', href: '/analytics', icon: LineChart, permission: 'analytics' },
      { title: 'IFTA', href: '/ifta', icon: Fuel, permission: 'ifta' },
    ]
  },
  { title: 'Customers', href: '/customers', icon: Building2, shortcut: getShortcutLabel('/customers'), permission: 'customers' },
  { title: 'Drivers', href: '/drivers', icon: Users, shortcut: getShortcutLabel('/drivers'), permission: 'drivers' },
  {
    title: 'Equipment',
    href: '/trucks',
    icon: Truck,
    permission: 'fleet',
    children: [
      { title: 'Trucks', href: '/trucks', icon: Truck, permission: 'fleet' },
      { title: 'Trailers', href: '/trailers', icon: Truck, permission: 'fleet' },
      { title: 'Equipment Map', href: '/tracking', icon: Map, permission: 'fleet' },
    ]
  },
  { title: 'Yards', href: '/yards', icon: Warehouse, permission: 'fleet',
    children: [
      { title: 'Yard Check', href: '/yards', icon: Warehouse, permission: 'fleet' },
      { title: 'Fleet Yards', href: '/yards/fleet-overview', icon: Container, permission: 'fleet' },
    ]
  },
  {
    title: 'Carriers',
    href: '/carriers',
    icon: Handshake,
    permission: 'carriers',
    children: [
      { title: 'All Carriers', href: '/carriers', icon: Handshake, permission: 'carriers' },
    ]
  },
  { title: 'Documents', href: '/documents', icon: FolderOpen, shortcut: getShortcutLabel('/documents'), permission: 'documents' },
  { title: 'Maintenance', href: '/maintenance', icon: Cog, permission: 'maintenance' },
  (() => {
    const connectedChildren: NavItem[] = [];
    if (integrations.qbConnected) connectedChildren.push({ title: 'QuickBooks', href: '/integrations/quickbooks', icon: BookOpen, permission: 'settings' as PermissionCategory });
    if (integrations.fleetrockConnected) connectedChildren.push({ title: 'Fleetrock', href: '/integrations/fleetrock', icon: Wrench, permission: 'settings' as PermissionCategory });
    if (integrations.relayConnected) connectedChildren.push({ title: 'Relay', href: '/integrations/relay', icon: Plug, permission: 'settings' as PermissionCategory });

    if (connectedChildren.length === 0) {
      // No integrations connected — single link to Settings > Integrations
      return { title: 'Integrations', href: '/settings?tab=integrations', icon: Plug, permission: 'settings' as PermissionCategory };
    }

    // Has connected integrations — show as expandable group
    return {
      title: 'Integrations',
      href: connectedChildren[0].href,
      icon: Plug,
      permission: 'settings' as PermissionCategory,
      children: connectedChildren,
    };
  })(),
  {
    title: 'Safety',
    href: '/compliance',
    icon: Shield,
    permission: 'compliance',
    children: [
      { title: 'Compliance', href: '/compliance', icon: Shield, permission: 'compliance' },
      { title: 'Inspections', href: '/inspections', icon: ClipboardCheck, permission: 'inspections' },
    ]
  },
  {
    title: 'Favorites',
    href: '/coming-soon?feature=Favorites',
    icon: Bookmark,
    permission: 'dashboard',
    children: []
  },
];

// Shared navigation component used in both desktop sidebar and mobile sheet
function SidebarNav({
  topActions,
  filteredNavigation,
  pathname,
  sidebarCollapsed = false,
  onNavigate,
  onSearchClick,
}: {
  topActions: NavItem[];
  filteredNavigation: NavItem[];
  pathname: string;
  sidebarCollapsed?: boolean;
  onNavigate?: () => void;
  onSearchClick?: () => void;
}) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (itemTitle: string, e?: React.MouseEvent) => {
    const isExpanding = !expandedSections[itemTitle];

    setExpandedSections(prev => ({
      ...prev,
      [itemTitle]: isExpanding
    }));

    if (isExpanding && e) {
      const parent = e.currentTarget.parentElement;
      if (parent) {
        setTimeout(() => {
          parent.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    }
  };

  const renderNavItem = (item: NavItem, isChild = false) => {
    const isActive = pathname === item.href || (item.children?.some(child => child.href === pathname));
    const Icon = item.icon;
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedSections[item.title];
    const isSearchItem = item.title === 'Search';

    // Handle Search item click
    const handleItemClick = (e: React.MouseEvent) => {
      if (isSearchItem && onSearchClick) {
        e.preventDefault();
        onSearchClick();
        onNavigate?.();
      } else {
        onNavigate?.();
      }
    };

    if (sidebarCollapsed) {
      // Search item in collapsed mode
      if (isSearchItem) {
        return (
          <Tooltip key={item.title}>
            <TooltipTrigger asChild>
              <button
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-md mx-auto',
                  'transition-colors hover:bg-blue-50'
                )}
                onClick={handleItemClick}
              >
                <Icon className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {item.title}
            </TooltipContent>
          </Tooltip>
        );
      }

      return (
        <Tooltip key={item.title}>
          <TooltipTrigger asChild>
            <Link
              to={item.href}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-md mx-auto',
                'transition-colors hover:bg-blue-50',
                isActive && 'bg-blue-100 text-blue-700'
              )}
              onClick={onNavigate}
            >
              <Icon className="h-4 w-4" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right">
            {item.title}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <div key={item.title}>
        {hasChildren ? (
          <>
            <button
              onClick={(e) => toggleSection(item.title, e)}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm',
                'transition-colors hover:bg-blue-50',
                isActive && 'bg-blue-100 text-blue-700',
                isChild && 'pl-10'
              )}
            >
              {!isChild && <Icon className="h-4 w-4 flex-shrink-0" />}
              <span className="flex-1 text-left font-normal">{item.title}</span>
              {item.badge && (
                item.badgeVariant === 'warning' ? (
                  <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-600">
                    <AlertTriangle className="h-3 w-3" />
                    {item.badge}
                  </span>
                ) : (
                  <span className={cn(
                    'px-2 py-0.5 text-[10px] font-medium rounded',
                    item.badge === 'Try Demo'
                      ? 'bg-blue-100 text-blue-600'
                      : item.badge === 'New'
                        ? 'bg-orange-100 text-orange-600'
                        : item.badge === 'Beta'
                          ? 'bg-orange-100 text-orange-600'
                          : 'bg-primary/10 text-primary'
                  )}>
                    {item.badge}
                  </span>
                )
              )}
              {item.shortcut && (
                <span className="text-xs text-muted-foreground">{item.shortcut}</span>
              )}
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              )}
            </button>
            {isExpanded && item.children && (
              <div className="mt-1 space-y-0.5">
                {item.children.map(child => renderNavItem(child, true))}
              </div>
            )}
          </>
        ) : isSearchItem ? (
          <button
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm w-full',
              'transition-colors hover:bg-accent/50',
              isChild && 'pl-10 text-[13px]'
            )}
            onClick={handleItemClick}
          >
            {!isChild && <Icon className="h-4 w-4 flex-shrink-0" />}
            <span className="flex-1 font-normal text-left">{item.title}</span>
            {item.shortcut && (
              <span className="text-xs text-muted-foreground flex-shrink-0">{item.shortcut}</span>
            )}
          </button>
        ) : item.openInNewWindow ? (
          <button
            onClick={() => {
              window.open(item.href, '_blank');
              onNavigate?.();
            }}
            className={cn(
              'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm cursor-pointer',
              'transition-colors hover:bg-blue-50',
              isChild && 'pl-10 text-[13px]'
            )}
          >
            {!isChild && <Icon className="h-4 w-4 flex-shrink-0" />}
            <span className="flex-1 font-normal text-left">{item.title}</span>
            <ExternalLink className="h-3.5 w-3.5 opacity-60" />
          </button>
        ) : (
          <Link
            to={item.href}
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm',
              'transition-colors hover:bg-blue-50',
              isActive && 'bg-blue-100 text-blue-700',
              isChild && 'pl-10 text-[13px]'
            )}
            onClick={onNavigate}
          >
            {!isChild && <Icon className="h-4 w-4 flex-shrink-0" />}
            <span className="flex-1 font-normal">{item.title}</span>
            {item.badge && (
              item.badgeVariant === 'warning' ? (
                <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-600 flex-shrink-0">
                  <AlertTriangle className="h-3 w-3" />
                  {item.badge}
                </span>
              ) : (
                <span className={cn(
                  'px-2 py-0.5 text-[10px] font-medium rounded flex-shrink-0',
                  item.badge === 'Try Demo'
                    ? 'bg-blue-100 text-blue-600'
                    : item.badge === 'New'
                      ? 'bg-orange-100 text-orange-600'
                      : item.badge === 'Beta'
                        ? 'bg-orange-100 text-orange-600'
                        : 'bg-blue-100 text-blue-600'
                )}>
                  {item.badge}
                </span>
              )
            )}
            {item.shortcut && (
              <span className="text-xs text-muted-foreground flex-shrink-0">{item.shortcut}</span>
            )}
          </Link>
        )}
      </div>
    );
  };

  // Split navigation into main and bottom sections
  const bottomTitles = ['QuickBooks', 'Help', 'Settings'];
  const mainNavItems = filteredNavigation.filter(item =>
    !bottomTitles.includes(item.title)
  );
  const bottomNavItems = filteredNavigation.filter(item =>
    bottomTitles.includes(item.title)
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top Actions (Home, Search, New Load) */}
      <nav className="flex-shrink-0 flex flex-col gap-0.5 p-2">
        {topActions.map((item) => renderNavItem(item))}
      </nav>

      {/* Divider */}
      {!sidebarCollapsed && <Separator className="mx-2 flex-shrink-0" />}

      {/* Main Navigation - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <nav className="flex flex-col gap-0.5 p-2">
          {mainNavItems.map((item) => renderNavItem(item))}
        </nav>
      </div>

      {/* Bottom Navigation - Messages & Help */}
      {!sidebarCollapsed && bottomNavItems.length > 0 && (
        <div className="flex-shrink-0 border-t p-2">
          <nav className="flex flex-col gap-0.5">
            {bottomNavItems.map((item) => renderNavItem(item))}
          </nav>
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { sidebarCollapsed, toggleSidebar, sidebarMobileOpen, setSidebarMobileOpen } = useUIStore();
  const { organization } = useOrganizationStore();
  const rbac = useRBAC();
  const loadPerms = useLoadPermissions();
  const { user } = useUserStore();
  const userRole = ((organization?.roles as string[])?.[0] || user?.role || 'driver') as UserRole;
  const [searchOpen, setSearchOpen] = useState(false);

  const topActions = getTopActions();

  const { data: qbStatus } = useQuickbooksStatus();
  const { data: fleetrockStatus } = useFleetrockStatus();
  const { data: relayStatus } = useRelayStatus();
  const businessMode = (organization?.businessMode || 'carrier') as 'carrier' | 'broker' | 'customer_portal';
  const navigation = getNavigation(businessMode, {
    qbConnected: !!qbStatus?.connected,
    fleetrockConnected: !!(fleetrockStatus as any)?.connected,
    relayConnected: !!(relayStatus as any)?.connected,
  });

  const handleSearchClick = () => {
    setSearchOpen(true);
  };

  // Filter navigation items and their children based on permissions
  const filterItems = (items: NavItem[]): NavItem[] => {
    return items.filter(item => {
      // Check business mode restrictions
      if (item.carrierOnly && businessMode !== 'carrier') return false;
      if (item.brokerOnly && businessMode !== 'broker') return false;

      // Check role-based permission using RBAC nav visibility
      if (item.permission) {
        if (item.isCreateAction) {
          // For create actions (Parts, New Load), check canCreate instead of canView
          const perm = hasPermission(userRole, item.permission, 'create');
          if (!perm || perm === 'view') return false;
        } else if (!rbac.canViewNav(item.permission as NavCategory)) {
          return false;
        }
      }

      // Filter children recursively
      if (item.children) {
        item.children = filterItems(item.children);
        // Hide parent if all children are filtered out
        if (item.children.length === 0) return false;
      }

      return true;
    });
  };

  // Add Help and Settings to navigation (Messages hidden)
  const allNavigation = [
    ...navigation,
    // { title: 'Messages', href: '/messaging', icon: MessageSquare, permission: 'messaging' as PermissionCategory },

    { title: 'Help', href: '/help', icon: HelpCircle, permission: 'dashboard' as PermissionCategory },
    { title: 'Settings', href: '/settings', icon: Settings, shortcut: getShortcutLabel('/settings'), permission: 'settings' as PermissionCategory },
  ];

  // Filter navigation based on permissions
  const filteredTopActions = filterItems(topActions);
  const filteredNavigation = filterItems(allNavigation);

  // Get company initials for logo
  const companyInitial = organization?.name?.charAt(0)?.toUpperCase() || 'T';

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile/Tablet Sheet - Slides in from left */}
      <Sheet open={sidebarMobileOpen} onOpenChange={setSidebarMobileOpen}>
        <SheetContent
          side="left"
          className="w-64 p-0 min-[860px]:hidden"
        >
          <VisuallyHidden>
            <SheetTitle>Navigation Menu</SheetTitle>
          </VisuallyHidden>
          <div className="flex h-14 items-center justify-between border-b px-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 shrink-0 truncate rounded bg-primary flex items-center justify-center text-primary-foreground font-semibold text-sm">
                {companyInitial}
              </div>
              <div className="flex flex-col">
                <span className="font-semibold text-sm leading-tight truncate">
                  {organization?.name || APP_NAME}
                </span>
                <span className="text-xs text-muted-foreground">
                  {organization?.dotNumber || ''}
                </span>
              </div>
            </div>
          </div>
          <SidebarNav
            topActions={filteredTopActions}
            filteredNavigation={filteredNavigation}
            pathname={pathname}
            onNavigate={() => setSidebarMobileOpen(false)}
            onSearchClick={handleSearchClick}
          />
        </SheetContent>
      </Sheet>

      {/* Global Search Dialog */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Desktop Sidebar - hidden on mobile and tablet, shown on desktop */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen border-r bg-background transition-all duration-300',
          'hidden min-[860px]:flex min-[860px]:flex-col',
          sidebarCollapsed ? 'w-16' : 'w-70'
        )}
      >
        <div className={`flex h-14 items-center justify-between border-b ${sidebarCollapsed ? ' px-0' : ' px-4'}`}>
          {!sidebarCollapsed ? (
            <>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="rounded shrink-0 flex items-center justify-center text-primary-foreground font-semibold text-sm">
                  {/* {companyInitial} */}
                  <img src={logo} className='w-20' alt="" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="font-semibold text-sm leading-tight truncate">
                    {organization?.name}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">
                    {organization?.dotNumber || ''}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={toggleSidebar}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="w-full"
              onClick={toggleSidebar}
            >
              <div className="rounded w-full flex items-center justify-center text-primary-foreground font-semibold text-xs">
                <img src={logo} className='w-13' alt="" />
              </div>
            </Button>
          )}
        </div>

        <SidebarNav
          topActions={filteredTopActions}
          filteredNavigation={filteredNavigation}
          pathname={pathname}
          sidebarCollapsed={sidebarCollapsed}
          onSearchClick={handleSearchClick}
        />
      </aside>
    </TooltipProvider>
  );
}
